import { useState, useCallback, useEffect, useMemo } from 'react';

enum USE_DEVICE_PERMISSIONS_ERROR_CODE {
	GENERIC = 'GENERIC',
	NOT_SUPPORTED = 'NOT_SUPPORTED',
	DEVICES_NOT_FOUND = 'DEVICES_NOT_FOUND',
	PERMISSION_DENIED = 'PERMISSION_DENIED',
}

interface UseDevicePermissionsError {
	code: USE_DEVICE_PERMISSIONS_ERROR_CODE;
	message?: string;
	error: unknown;
}

const useDevicePermissionsErrors: Record<
	USE_DEVICE_PERMISSIONS_ERROR_CODE,
	(error: unknown) => UseDevicePermissionsError
> = {
	[USE_DEVICE_PERMISSIONS_ERROR_CODE.GENERIC]: (error) => ({
		code: USE_DEVICE_PERMISSIONS_ERROR_CODE.GENERIC,
		error,
	}),
	[USE_DEVICE_PERMISSIONS_ERROR_CODE.NOT_SUPPORTED]: (error) => ({
		code: USE_DEVICE_PERMISSIONS_ERROR_CODE.NOT_SUPPORTED,
		error,
	}),
	[USE_DEVICE_PERMISSIONS_ERROR_CODE.DEVICES_NOT_FOUND]: (error) => ({
		code: USE_DEVICE_PERMISSIONS_ERROR_CODE.DEVICES_NOT_FOUND,
		error,
	}),
	[USE_DEVICE_PERMISSIONS_ERROR_CODE.PERMISSION_DENIED]: (error) => ({
		code: USE_DEVICE_PERMISSIONS_ERROR_CODE.PERMISSION_DENIED,
		error,
	}),
};

const isCustomUseDevicePermissionsError = (error: unknown): error is UseDevicePermissionsError => {
	if (typeof error !== 'object' || error === null) {
		return false;
	}

	return Object.prototype.hasOwnProperty.call(error, 'code') && Object.prototype.hasOwnProperty.call(error, 'error');
};

export enum DEVICE_PERMISSION_STATE {
	DENIED = 'DENIED',
	GRANTED = 'GRANTED',
	PROMPT = 'PROMPT',
}

type DevicePermissionKind = 'microphone' | 'camera';

export const useDevicePermissions = (options?: {
	autoRequestOnMount?: boolean;
	kind?: DevicePermissionKind; // default 'microphone'
}) => {
	const [devicePermissionState, setDevicePermissionState] = useState<DEVICE_PERMISSION_STATE>(
		DEVICE_PERMISSION_STATE.PROMPT
	);
	const [devicePermissionError, setDevicePermissionError] = useState<UseDevicePermissionsError>();

	const autoRequestOnMount = options?.autoRequestOnMount ?? true;
	const mediaKind: DevicePermissionKind = options?.kind ?? 'microphone';
	const constraints = useMemo<MediaStreamConstraints>(() => {
		return mediaKind === 'camera' ? { video: true } : { audio: true };
	}, [mediaKind]);

	const getFormattedError = useCallback((error: unknown, fallbackErrorCode: USE_DEVICE_PERMISSIONS_ERROR_CODE) => {
		const formattedError = isCustomUseDevicePermissionsError(error)
			? error
			: useDevicePermissionsErrors[fallbackErrorCode](error);

		return formattedError;
	}, []);

	const promptForDevicePermission = useCallback(async (): Promise<MediaStream> => {
		try {
			const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
			return mediaStream; // do NOT stop tracks here if caller intends to use the stream
		} catch (error) {
			throw getFormattedError(error, USE_DEVICE_PERMISSIONS_ERROR_CODE.PERMISSION_DENIED);
		}
	}, [constraints, getFormattedError]);

	const checkConnectedDevices = useCallback(async () => {
		try {
			const devices = await navigator.mediaDevices.enumerateDevices();
			const usableDevices = devices.filter((device) => {
				if (!device.label || !device.deviceId) return false;
				if (mediaKind === 'camera') return device.kind === 'videoinput';
				return device.kind === 'audioinput';
			});

			if (usableDevices.length <= 0) {
				throw getFormattedError(undefined, USE_DEVICE_PERMISSIONS_ERROR_CODE.DEVICES_NOT_FOUND);
			}
		} catch (error) {
			throw getFormattedError(error, USE_DEVICE_PERMISSIONS_ERROR_CODE.GENERIC);
		}
	}, [getFormattedError, mediaKind]);

	const checkDevicePermissions = useCallback(async () => {
		const permissionsApiSupported = 'permissions' in navigator && 'query' in navigator.permissions;

		if (!permissionsApiSupported) {
			// Permissions API not supported (e.g., Firefox): treat as unknown; caller will infer from getUserMedia success
			return DEVICE_PERMISSION_STATE.PROMPT;
		}

		try {
			const permissionName = (mediaKind === 'camera' ? 'camera' : 'microphone') as PermissionName;
			const status = await navigator.permissions.query({ name: permissionName });
			if (status.state === 'granted') return DEVICE_PERMISSION_STATE.GRANTED;
			if (status.state === 'prompt') return DEVICE_PERMISSION_STATE.PROMPT;
			return DEVICE_PERMISSION_STATE.DENIED;
		} catch {
			return DEVICE_PERMISSION_STATE.PROMPT;
		}
	}, [mediaKind]);

	const handleAccessButtonClick = useCallback(
		async (successCallback?: (stream: MediaStream) => void, errorCallback?: () => void) => {
			try {
				// If getUserMedia succeeds, the user granted access for this session.
				const stream = await promptForDevicePermission();
				await checkConnectedDevices();

				// Optimistically mark granted after successful prompt, for browsers without Permissions API support.
				setDevicePermissionState(DEVICE_PERMISSION_STATE.GRANTED);
				setDevicePermissionError(undefined);

				// Best-effort refine using Permissions API; ignore failures and keep GRANTED.
				try {
					const inferredState = await checkDevicePermissions();
					setDevicePermissionState(
						inferredState === DEVICE_PERMISSION_STATE.PROMPT
							? DEVICE_PERMISSION_STATE.GRANTED
							: inferredState
					);
				} catch {
					// No-op
				}

				successCallback?.(stream);
			} catch (error) {
				setDevicePermissionState(DEVICE_PERMISSION_STATE.DENIED);
				setDevicePermissionError(getFormattedError(error, USE_DEVICE_PERMISSIONS_ERROR_CODE.GENERIC));
				errorCallback?.();
			}
		},
		[checkConnectedDevices, checkDevicePermissions, getFormattedError, promptForDevicePermission]
	);

	useEffect(() => {
		if (autoRequestOnMount) {
			handleAccessButtonClick();
		}
	}, [handleAccessButtonClick, autoRequestOnMount]);

	return { devicePermissionState, devicePermissionError, handleAccessButtonClick };
};
